/**
 * RouteFallback - route-level Suspense skeleton (2026-06-10, UI/UX pass).
 *
 * WHY: every lazy route in build-routes.tsx used `fallback={null}`, which
 * renders a dead blank viewport while a route chunk downloads. Sub-100ms on
 * a warm LAN, but a visible dead frame on cold cache / slow links - exactly
 * the state the app's skeleton system exists to cover. This component is a
 * generic page-shaped shimmer (title bar + content rows) used as the shared
 * Suspense fallback so chunk loads telegraph "page is coming" in the same
 * visual language as the in-page loaders.
 *
 * Bundle note: imported by build-routes.tsx, so it ships in the pre-auth
 * entry chunk BY DESIGN (the fallback must exist before any lazy chunk
 * arrives). It composes only the entry-friendly Skeleton component and
 * carries no admin code, so the bundle-isolation guard is unaffected.
 *
 * Accessibility: a single role="status" region labelled "Loading page" -
 * the individual bars are decorative (Skeleton sets aria-hidden), matching
 * the SkeletonTableRows contract. Reduced motion halts the shimmer at the
 * CSS layer (Skeleton.css).
 */
import { type JSX } from 'react';
import { Skeleton } from './Skeleton/Skeleton';

/**
 * Page-shaped skeleton rendered while a lazy route chunk loads.
 *
 * @returns The fallback element for route-level <Suspense> boundaries.
 */
export function RouteFallback(): JSX.Element {
  return (
    <div
      role="status"
      aria-label="Loading page"
      aria-live="polite"
      data-testid="route-fallback"
      style={{
        // Inline layout-only styles: this is a transient frame, not a themed
        // surface; the Skeleton class supplies all color/motion treatment.
        display: 'flex',
        flexDirection: 'column',
        gap: '14px',
        padding: '28px 32px',
        maxWidth: '1100px',
        margin: '0 auto',
        width: '100%',
      }}
    >
      <Skeleton width={220} height={22} />
      <Skeleton width={340} height={12} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
        <Skeleton height={36} />
        <Skeleton height={36} />
        <Skeleton height={36} />
        <Skeleton height={36} />
      </div>
    </div>
  );
}

export default RouteFallback;
